import {
  Popover,
  InteractiveContainer,
  PlainButton,
  Button,
  TextInput,
} from "@jugl-web/ui-components/cross-platform";
import { useTranslations } from "@jugl-web/utils";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ReactComponent as AddIcon } from "./assets/add.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { MAX_NAME_LENGTH, MAX_VALUE_LENGTH } from "../CustomField/constants";

export type CustomFieldButtonProps = {
  onSave: (name: string, value: string) => void;
};

type FormFields = {
  name: string;
  value: string;
};

export const CustomFieldButton = ({ onSave }: CustomFieldButtonProps) => {
  const { t } = useTranslations();

  const notEmptyMessage = t({
    id: "common.validation.notEmpty",
    defaultMessage: "Value cannot be empty",
  });

  const maxLengthMessage = t({
    id: "invoices.settings.businessInformation.customField.maxLength",
    defaultMessage: "Field name must be 30 characters or fewer",
  });

  const schema = yup.object().shape({
    name: yup
      .string()
      .trim()
      .required(notEmptyMessage)
      .test(
        "max-length",
        maxLengthMessage,
        (value) => !value || value.length <= MAX_NAME_LENGTH
      ),
    value: yup
      .string()
      .trim()
      .required(notEmptyMessage)
      .test(
        "max-length",
        maxLengthMessage,
        (value) => !value || value.length <= MAX_VALUE_LENGTH
      ),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<FormFields>({
    resolver: yupResolver(schema),
    mode: "onChange",
  });

  const resetForm = () => {
    reset();
  };

  const onFormSubmit = (data: FormFields) => {
    onSave(data.name, data.value);
    resetForm();
  };

  return (
    <Popover
      placement="right-start"
      className="p-6"
      renderTrigger={({ Trigger, triggerRef }) => (
        <Trigger ref={triggerRef} as={InteractiveContainer} className="w-10">
          <Button
            color="grey"
            className="text-dark-600 font-poppins text-sm font-normal"
            iconStart={<AddIcon />}
          >
            Add
          </Button>
        </Trigger>
      )}
    >
      {({ onClose }) => (
        <form
          className="flex flex-col gap-4"
          onSubmit={handleSubmit((data) => {
            onFormSubmit(data);
            onClose();
          })}
        >
          <span className="flex items-center justify-between font-semibold text-[#383838]">
            <div>
              {t({
                id: "invoices.settings.businessInformation.customField.title",
                defaultMessage: "Add custom field",
              })}
            </div>
            <PlainButton onClick={onClose}>
              <CloseIcon />
            </PlainButton>
          </span>
          <div className="flex w-screen max-w-sm flex-col gap-5">
            <TextInput
              placeholder={t({
                id: "common.enter",
                defaultMessage: "Enter",
              })}
              label={t({
                id: "invoices.settings.businessInformation.customField.name",
                defaultMessage: "Field name",
              })}
              errorMessage={errors.name?.message}
              {...register("name")}
            />

            <TextInput
              placeholder={t({
                id: "common.enter",
                defaultMessage: "Enter",
              })}
              label={t({
                id: "invoices.settings.businessInformation.customField.value",
                defaultMessage: "Field value",
              })}
              errorMessage={errors.value?.message}
              {...register("value")}
            />

            <div className="mt-3 grid grid-cols-2 gap-3">
              <Button
                type="button"
                color="grey"
                className="h-10 w-full"
                onClick={() => {
                  resetForm();
                  onClose();
                }}
              >
                {t({
                  id: "common.close",
                  defaultMessage: "Close",
                })}
              </Button>
              <Button
                type="submit"
                color="primary"
                isDisabled={!isValid}
                className="h-10 w-full"
              >
                {t({
                  id: "common.save",
                  defaultMessage: "Save",
                })}
              </Button>
            </div>
          </div>
        </form>
      )}
    </Popover>
  );
};
